import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import {
  blocsHoraires,
  blocsHorairesAvecComplet,
  joursCombines,
  libellePastille,
} from './creneaux';
import {
  BoutonVert,
  Compteur,
  Corps,
  Encart,
  LARGE,
  Marge,
  Pied,
  SousTitre,
  Titre,
  mobile,
} from './ui';

/**
 * Étape 2, atelier en DEMANDE À CONFIRMER : le client propose de 1 à 10
 * créneaux sur 21 jours, l'atelier en retient un.
 *
 * Reprise du sélecteur de l'app mobile (`rj-mobile/src/components/ui/
 * slots-picker.tsx`) à la demande de Clément (« ne pas réinventer la roue ») :
 * pastilles de jours avec le nombre de créneaux choisis, « Toute la journée »,
 * grille de blocs d'une heure, compteur et « Continuer » dans le pied.
 *
 * Une heure complète (`full_slots`, FIXERLIGHT-053) s'affiche grisée dans la
 * même grille, non sélectionnable, distincte d'une heure hors horaires qui ne
 * produit aucun bloc (FIXERLIGHT-070).
 */

// Une seule ligne qui défile, à toutes les largeurs : passer à la ligne sur
// ordinateur empilait trois semaines de pastilles (retour de Clément du 22/09).
// Sur ordinateur, deux flèches font défiler la bande.
const Bande = styled.div`
  position: relative;
  margin-bottom: 16px;

  @media (min-width: ${LARGE}) {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 32px;
  }
`;

const Jours = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 16px;
  scroll-behavior: smooth;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: ${LARGE}) {
    flex: 1;
    min-width: 0;
    padding: 0;
    /* Un fondu du côté où il reste des jours, plutôt qu'une pastille coupée net. */
    mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 ${(p) => (p.$debut ? '0' : '40px')},
      #000 calc(100% - ${(p) => (p.$fin ? '0px' : '40px')}),
      transparent 100%
    );
  }
`;

const Fleche = styled.button`
  display: none;

  @media (min-width: ${LARGE}) {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 20px;
    border: 1.5px solid ${mobile.border};
    background: ${colors.white};
    color: ${colors.textDark};
    font-size: 18px;
    cursor: pointer;

    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
`;

const Pastille = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 20px;
  background: ${(p) => (p.$on ? colors.bleu : mobile.muted)};
  color: ${(p) => (p.$on ? colors.white : colors.textDark)};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
`;

const Nombre = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.$on ? colors.vert : colors.bleu)};
  color: ${(p) => (p.$on ? colors.textDark : colors.white)};
`;

const ToutLeJour = styled.button`
  display: block;
  width: 100%;
  min-height: 48px;
  margin-bottom: 16px;
  border-radius: 14px;
  border: 1.5px solid ${(p) => (p.$on ? colors.vert : mobile.border)};
  background: ${(p) => (p.$on ? mobile.highlight : colors.white)};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  color: ${(p) => (p.$on ? colors.textDark : colors.bleu)};
  cursor: pointer;
`;

// Grille, Bloc et Complet servent aussi « Déplacer le rendez-vous » de
// l'espace client (FIX-1155) : les mêmes blocs d'une heure qu'ici.
export const Grille = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (min-width: ${LARGE}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const Bloc = styled.button`
  min-height: 52px;
  border-radius: 14px;
  border: ${(p) =>
    p.$complet
      ? `1.5px dashed ${mobile.border}`
      : p.$on
      ? `2px solid ${colors.vert}`
      : `1.5px solid ${mobile.border}`};
  background: ${(p) =>
    p.$complet ? mobile.muted : p.$on ? mobile.highlight : colors.white};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  color: ${(p) => (p.$complet ? mobile.mutedText : colors.textDark)};
  cursor: ${(p) => (p.$complet ? 'default' : 'pointer')};
  font-variant-numeric: tabular-nums;

  &:disabled {
    opacity: 1;
  }
`;

/** « Complet », sous le libellé de l'heure, dans un bloc grisé. */
export const Complet = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const libelleCompteur = (n) => {
  if (n === 0) return 'Sélectionnez au moins un créneau';
  if (n === 1)
    return (
      <>
        <b>1 créneau</b> sélectionné
      </>
    );
  return (
    <>
      <b>{n} créneaux</b> sélectionnés
    </>
  );
};

export default function EtapeCreneaux({
  slots,
  fullSlots = [],
  selected,
  onToggle,
  onToutLeJour,
  jourInitial = null,
  refus,
  alerte,
  onContinuer,
  titreRef,
}) {
  const jours = useMemo(() => joursCombines(slots, fullSlots), [
    slots,
    fullSlots,
  ]);
  // Un jour entièrement complet n'a rien à proposer : ce n'est jamais lui qui
  // s'ouvre par défaut. On préfère le premier jour qui a au moins une heure
  // libre, et seulement à défaut le premier jour tout court (tous complets).
  const premierJourUtile = (liste) =>
    liste.find((j) => j.creneaux.length) || liste[0] || null;
  const [jourCle, setJourCle] = useState(() => {
    if (jours.some((j) => j.cle === jourInitial)) return jourInitial;
    const utile = premierJourUtile(jours);
    return utile ? utile.cle : null;
  });

  useEffect(() => {
    if (!jours.find((j) => j.cle === jourCle) && jours.length) {
      setJourCle(premierJourUtile(jours).cle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jours, jourCle]);

  const jour = jours.find((j) => j.cle === jourCle);
  // Seuls les créneaux LIBRES du jour comptent pour la sélection et « Toute
  // la journée » : un bloc complet n'a rien à proposer à l'api.
  const isosDuJour = jour ? blocsHoraires(jour.creneaux).map((b) => b.iso) : [];
  const blocs = jour
    ? blocsHorairesAvecComplet(jour.creneaux, jour.creneauxComplets)
    : [];
  const toutChoisi =
    isosDuJour.length > 0 && isosDuJour.every((iso) => selected.includes(iso));

  const bande = useRef(null);
  const [bords, setBords] = useState({ debut: true, fin: true });
  const mesurer = () => {
    const el = bande.current;
    if (!el) return;
    const debut = el.scrollLeft <= 1;
    const fin = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    // Même valeur, même objet : pas de rendu à chaque image du défilement.
    setBords((b) => (b.debut === debut && b.fin === fin ? b : { debut, fin }));
  };
  useEffect(() => {
    mesurer();
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [jours.length]);
  // Le jour choisi depuis la fiche peut être loin dans la bande : on l'amène
  // à l'écran à l'ouverture.
  useEffect(() => {
    const el = bande.current;
    const on = el && el.querySelector('[aria-pressed="true"]');
    if (on && typeof on.scrollIntoView === 'function') {
      on.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const defiler = (sens) => {
    const el = bande.current;
    if (el) el.scrollBy({ left: sens * el.clientWidth * 0.8 });
  };

  const blocsParJour = useMemo(
    () =>
      new Map(
        jours.map((j) => [j.cle, blocsHoraires(j.creneaux).map((b) => b.iso)]),
      ),
    [jours],
  );
  const nbParJour = (j) =>
    blocsParJour.get(j.cle).filter((iso) => selected.includes(iso)).length;

  return (
    <>
      <Corps $flush>
        <Marge>
          <Titre tabIndex={-1} ref={titreRef}>
            Vos disponibilités
          </Titre>
          <SousTitre>
            Sélectionnez plusieurs créneaux pour obtenir un rendez-vous plus
            rapidement.
          </SousTitre>
          {alerte && (
            <Encart $ton="alerte" role="alert">
              {alerte}
            </Encart>
          )}
          <Encart>
            <span aria-hidden="true">ⓘ</span>
            <span>
              Proposez vos disponibilités : l’atelier en retient une et vous la
              confirme par email.
            </span>
          </Encart>
        </Marge>

        <Bande>
          <Fleche
            type="button"
            aria-label="Jours précédents"
            disabled={bords.debut}
            onClick={() => defiler(-1)}>
            ‹
          </Fleche>
          <Jours
            role="group"
            aria-label="Jours"
            ref={bande}
            $debut={bords.debut}
            $fin={bords.fin}
            onScroll={mesurer}>
            {jours.map((j) => {
              const on = j.cle === jourCle;
              const n = nbParJour(j);
              return (
                <Pastille
                  key={j.cle}
                  type="button"
                  aria-pressed={on}
                  $on={on}
                  onClick={() => setJourCle(j.cle)}>
                  {libellePastille(j.creneaux[0] || j.creneauxComplets[0])}
                  {n > 0 && (
                    <Nombre $on={on} aria-label={`${n} choisis`}>
                      {n}
                    </Nombre>
                  )}
                </Pastille>
              );
            })}
          </Jours>
          <Fleche
            type="button"
            aria-label="Jours suivants"
            disabled={bords.fin}
            onClick={() => defiler(1)}>
            ›
          </Fleche>
        </Bande>

        <Marge>
          <ToutLeJour
            type="button"
            $on={toutChoisi}
            aria-pressed={toutChoisi}
            onClick={() => onToutLeJour(isosDuJour, toutChoisi)}>
            {toutChoisi ? 'Toute la journée ✓' : 'Toute la journée'}
          </ToutLeJour>
          <Grille>
            {blocs.map((b) => {
              const on = !b.complet && selected.includes(b.iso);
              return (
                <Bloc
                  key={`${jourCle}-${b.heure}`}
                  type="button"
                  $on={on}
                  $complet={b.complet}
                  disabled={b.complet}
                  aria-pressed={b.complet ? undefined : on}
                  aria-label={b.complet ? `${b.libelle}, complet` : undefined}
                  onClick={() => !b.complet && onToggle(b.iso)}>
                  {b.libelle}
                  {b.complet ? <Complet>Complet</Complet> : on && ' ✓'}
                </Bloc>
              );
            })}
          </Grille>
        </Marge>
      </Corps>
      <Pied>
        {refus && (
          <Encart $ton="alerte" role="alert" style={{ textAlign: 'left' }}>
            {refus}
          </Encart>
        )}
        <Compteur aria-live="polite">
          {libelleCompteur(selected.length)}
        </Compteur>
        <BoutonVert
          type="button"
          disabled={selected.length === 0}
          onClick={onContinuer}>
          Continuer
        </BoutonVert>
      </Pied>
    </>
  );
}
